import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Eye, History } from 'lucide-react';
import { usePromptDraft, useUpdatePromptDraft } from '@/hooks/usePromptDraft';
import { usePublishPrompt } from '@/hooks/usePromptVersions';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-handler';
import { PromptStatusBadge } from './PromptStatusBadge';
import { PromptSectionAccordion } from './PromptSectionAccordion';
import { PromptSectionEditor } from './PromptSectionEditor';
import { PromptPreviewSheet } from './PromptPreviewSheet';
import { PromptHistorySheet } from './PromptHistorySheet';
import { PromptPublishButton } from './PromptPublishButton';
import type { PromptSectionName } from '@/types/api/prompt-versions';

export function PromptPersonalizationTab() {
  const { toast } = useToast();

  // State
  const [editingSection, setEditingSection] = useState<PromptSectionName | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Queries & Mutations
  const { data: draft, isLoading, error } = usePromptDraft();
  const updateMutation = useUpdatePromptDraft();
  const publishMutation = usePublishPrompt();

  // Handlers
  const handleSaveSection = async (section: PromptSectionName, value: string | null) => {
    try {
      await updateMutation.mutateAsync({ [section]: value });
      setEditingSection(null);
      toast({
        title: 'Salvo',
        description: 'Rascunho atualizado com sucesso',
      });
    } catch (err) {
      toast({
        title: 'Erro',
        description: getErrorMessage(err, 'Erro ao salvar rascunho'),
        variant: 'destructive',
      });
    }
  };

  const handlePublish = async () => {
    try {
      const result = await publishMutation.mutateAsync();
      toast({
        title: 'Publicado',
        description: `Versão ${result.versionNumber}.0 publicada com sucesso`,
      });
    } catch (err) {
      toast({
        title: 'Erro',
        description: getErrorMessage(err, 'Erro ao publicar versão'),
        variant: 'destructive',
      });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error || !draft) {
    return (
      <Card>
        <CardHeader className="text-center py-8">
          <CardTitle className="text-destructive">Erro ao carregar rascunho</CardTitle>
          <CardDescription>
            {(error instanceof Error ? error.message : undefined) || 'Não foi possível carregar o rascunho do prompt'}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const sections = {
    identity: draft.identity,
    mission: draft.mission,
    tone: draft.tone,
    capabilities: draft.capabilities,
    responseRules: draft.responseRules,
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-lg">Personalização do Prompt</CardTitle>
              <CardDescription className="mt-1">
                Customize como o assistente se apresenta e responde
              </CardDescription>
            </div>
            <PromptStatusBadge
              hasChanges={draft.hasChanges}
              currentVersion={draft.currentVersion}
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Sections Accordion */}
          <PromptSectionAccordion
            sections={sections}
            onEditSection={setEditingSection}
          />

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
            <div className="flex gap-2 flex-1">
              <Button
                variant="outline"
                onClick={() => setShowPreview(true)}
                className="flex-1 sm:flex-none"
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowHistory(true)}
                className="flex-1 sm:flex-none"
              >
                <History className="h-4 w-4 mr-2" />
                Histórico
              </Button>
            </div>
            <PromptPublishButton
              currentVersion={draft.currentVersion}
              hasChanges={draft.hasChanges}
              onPublish={handlePublish}
              isPublishing={publishMutation.isPending}
            />
          </div>
        </CardContent>
      </Card>

      {/* Editor Sheet */}
      <PromptSectionEditor
        open={!!editingSection}
        onOpenChange={(open) => !open && setEditingSection(null)}
        section={editingSection}
        value={editingSection ? sections[editingSection] : null}
        onSave={handleSaveSection}
        isSaving={updateMutation.isPending}
      />

      {/* Preview Sheet */}
      <PromptPreviewSheet
        open={showPreview}
        onOpenChange={setShowPreview}
      />

      {/* History Sheet */}
      <PromptHistorySheet
        open={showHistory}
        onOpenChange={setShowHistory}
      />
    </>
  );
}
